import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Theme ────────────────────────────────────────────────────────────────────
const PRIMARY      = '#0d47a1';
const PRIMARY_MID  = '#1565c0';
const PRIMARY_DARK = '#0a3880';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).split('T')[0];
  const parts = s.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return s;
};

const isCollectedStatus = (s) => {
  const v = String(s || '').toLowerCase();
  return v === 'collected' || v === 'received';
};

const paymentStatusBadge = (s) => {
  const v = String(s || '').toLowerCase();
  if (isCollectedStatus(v)) return { bg: '#d1fae5', color: '#059669' };
  if (v === 'pending')      return { bg: '#fef3c7', color: '#d97706' };
  if (v === 'partial')      return { bg: '#dbeafe', color: '#1d4ed8' };
  if (v === 'failed')       return { bg: '#fee2e2', color: '#dc2626' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

const verificationBadge = (s) => {
  const v = String(s || '').toLowerCase();
  if (v === 'verified')   return { bg: '#d1fae5', color: '#059669' };
  if (v === 'pending')    return { bg: '#fef3c7', color: '#d97706' };
  if (v === 'rejected')   return { bg: '#fee2e2', color: '#dc2626' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

// ─── TmpType options ──────────────────────────────────────────────────────────
const TMP_TYPES = [
  { value: 'All',       label: 'All' },
  { value: 'Pending',   label: 'Pending' },
  { value: 'Received',  label: 'Received' },
  { value: 'Partial',   label: 'Partial' },
];

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.15)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CashOnDeliveryPaymentStatusReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = new Date();
  const [fromDate,  setFromDate]  = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate,    setToDate]    = useState(fmt2Date(today));
  const [tmpType,   setTmpType]   = useState('All');
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [fetched,   setFetched]   = useState(false);
  const [search,    setSearch]    = useState('');

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select a date range'); return; }
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetchData(
        'ReportApi',
        `/CashOnDeliveryPaymentStatus/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res?.response_data ?? (Array.isArray(res) ? res : []);
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('CashOnDeliveryPaymentStatusReport error:', err);
      setError(`Failed to fetch report: ${err?.response?.status ? `HTTP ${err.response.status}` : err?.message || 'Unknown error'}`);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token]); // eslint-disable-line

  // ─── Client-side search ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.BillNo          || '').toLowerCase().includes(q) ||
      (r.Customer        || '').toLowerCase().includes(q) ||
      (r.DeliveryStaff   || '').toLowerCase().includes(q) ||
      (r.PaymentStatus   || '').toLowerCase().includes(q) ||
      (r.ReceiptNo       || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // ─── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => filtered.reduce((acc, r) => {
    // When payment is received/collected, ReceiptAmount may be 0 — fall back to ToCollect
    const received = isCollectedStatus(r.PaymentStatus);
    const receiptAmt = parseFloat(r.ReceiptAmount) || (received ? (parseFloat(r.ToCollect) || 0) : 0);
    return {
      BillAmount:    acc.BillAmount    + (parseFloat(r.BillAmount) || 0),
      ToCollect:     acc.ToCollect     + (parseFloat(r.ToCollect)  || 0),
      ReceiptAmount: acc.ReceiptAmount + receiptAmt,
    };
  }, { BillAmount: 0, ToCollect: 0, ReceiptAmount: 0 }), [filtered]);

  // ─── Excel export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'CashOnDeliveryPaymentStatusReport', 0);
    if (!filtered.length) return;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('COD Payment Status');
    const COLS = 15;

    // frozen header rows
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 5 }];

    const thin = {
      top: { style: 'thin' }, left: { style: 'thin' },
      bottom: { style: 'thin' }, right: { style: 'thin' },
    };
    const merge = (r, c1, c2) => ws.mergeCells(r, c1, r, c2);
    const cell  = (r, c) => ws.getCell(r, c);
    const setH  = (r, h) => { ws.getRow(r).height = h; };

    // ── Header rows ────────────────────────────────────────────────────────
    let row = 1;
    merge(row, 1, COLS);
    Object.assign(cell(row, 1), {
      value: companyName || 'Company',
      font: { bold: true, size: 14, color: { argb: 'FF0a3880' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe3f2fd' } },
    });
    setH(row, 22); row++;

    merge(row, 1, COLS);
    Object.assign(cell(row, 1), {
      value: 'CASH ON DELIVERY PAYMENT STATUS REPORT',
      font: { bold: true, size: 12, color: { argb: 'FF0d47a1' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    });
    setH(row, 18); row++;

    merge(row, 1, COLS);
    Object.assign(cell(row, 1), {
      value: `Period: ${fmtDate(fromDate)} to ${fmtDate(toDate)}  |  Type: ${tmpType}`,
      font: { size: 10, color: { argb: 'FF37474f' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    });
    setH(row, 16); row++;

    merge(row, 1, COLS);
    Object.assign(cell(row, 1), {
      value: `Generated on: ${new Date().toLocaleString('en-IN')}  |  By: ${username || ''}`,
      font: { italic: true, size: 9, color: { argb: 'FF9e9e9e' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
    });
    setH(row, 14); row++;

    // ── Column header row ───────────────────────────────────────────────────
    const headers = [
      '#', 'Bill No', 'Bill Date', 'Customer',
      'Bill Amount', 'To Collect', 'Payment Status', 'Payment Mode',
      'Collected Date', 'Verification', 'Remarks',
      'Receipt No', 'Receipt Date', 'Receipt Amount', 'Receipt Pay Mode',
    ];
    const hdrRow = ws.getRow(row);
    hdrRow.values = ['', ...headers]; // col 1 is blank placeholder, ExcelJS is 1-indexed
    hdrRow.values = headers;          // overwrite properly
    headers.forEach((h, ci) => {
      const c = cell(row, ci + 1);
      c.value = h;
      c.font      = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } };
      c.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0a3880' } };
      c.alignment = { horizontal: [4, 5, 6, 13].includes(ci) ? 'right' : 'center', vertical: 'middle' };
      c.border    = thin;
    });
    setH(row, 18); row++;

    // ── Column widths ───────────────────────────────────────────────────────
    const widths = [4, 14, 12, 24, 13, 12, 14, 12, 14, 14, 18, 13, 13, 14, 14];
    widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    // ── Helper: write an amount cell ────────────────────────────────────────
    const writeAmt = (r, c, val, fontColor, bgArgb, bdr) => {
      const cl = cell(r, c);
      cl.value     = parseFloat(val) || 0;
      cl.numFmt    = '#,##0.00';
      cl.font      = { bold: true, size: 9, color: { argb: fontColor || 'FF000000' } };
      cl.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb || 'FFFFFFFF' } };
      cl.alignment = { horizontal: 'right', vertical: 'middle' };
      cl.border    = bdr || thin;
    };

    // ── Helper: write a label cell ──────────────────────────────────────────
    const writeLabel = (r, c1, c2, text, fontColor, bgArgb, bdr, fontSize) => {
      if (c2 > c1) merge(r, c1, c2);
      const cl = cell(r, c1);
      cl.value     = text;
      cl.font      = { bold: true, size: fontSize || 9, color: { argb: fontColor || 'FF000000' } };
      cl.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb || 'FFFFFFFF' } };
      cl.alignment = { horizontal: 'left', vertical: 'middle' };
      cl.border    = bdr || thin;
    };

    // ── Data rows ───────────────────────────────────────────────────────────
    let globalIdx = 0;

    groupedByBranch.forEach(([branchName, staffGroups]) => {
      const allBranchRows = staffGroups.flatMap(([, rows]) => rows);

      // Branch header
      merge(row, 1, COLS);
      const bHdr = cell(row, 1);
      bHdr.value     = `  🏢  ${branchName}  (${allBranchRows.length} records)`;
      bHdr.font      = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
      bHdr.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0a3880' } };
      bHdr.alignment = { horizontal: 'left', vertical: 'middle' };
      setH(row, 16); row++;

      staffGroups.forEach(([staffName, staffRows]) => {

        // Staff sub-header
        merge(row, 1, COLS);
        const sHdr = cell(row, 1);
        sHdr.value     = `      🚚  ${staffName}  (${staffRows.length} bill${staffRows.length !== 1 ? 's' : ''})`;
        sHdr.font      = { bold: true, size: 10, color: { argb: 'FF0d47a1' } };
        sHdr.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8eaf6' } };
        sHdr.alignment = { horizontal: 'left', vertical: 'middle' };
        sHdr.border    = { top: { style: 'thin', color: { argb: 'FFc5cae9' } }, bottom: { style: 'thin', color: { argb: 'FFc5cae9' } } };
        setH(row, 15); row++;

        // Data rows
        staffRows.forEach((r, i) => {
          globalIdx++;
          const bg = i % 2 === 0 ? 'FFFFFFFF' : 'FFf3f4fd';
          const receiptAmt = parseFloat(r.ReceiptAmount) || (isCollectedStatus(r.PaymentStatus) ? parseFloat(r.ToCollect) : 0);

          const vals = [
            globalIdx,
            r.BillNo || '',
            fmtDate(r.BillDate),
            r.Customer || '',
            parseFloat(r.BillAmount) || 0,
            parseFloat(r.ToCollect)  || 0,
            r.PaymentStatus      || '',
            r.PaymentMode        || '',
            r.CollectedDate      ? fmtDate(r.CollectedDate) : '-',
            r.VerificationStatus || '',
            r.Remarks            || '',
            r.ReceiptNo          || '',
            r.ReceiptDate        ? fmtDate(r.ReceiptDate)   : '-',
            receiptAmt,
            r.ReceiptPaymentMode || '',
          ];

          vals.forEach((v, ci) => {
            const c = cell(row, ci + 1);
            c.value  = v;
            c.font   = { size: 9 };
            c.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
            c.border = thin;
            c.alignment = { vertical: 'middle', horizontal: [4, 5, 13].includes(ci) ? 'right' : 'left' };
            if (ci === 4 || ci === 5 || ci === 13) c.numFmt = '#,##0.00';
          });
          setH(row, 14); row++;
        });

        // Staff subtotal
        const ss = calcSubtotal(staffRows);
        writeLabel(row, 1, 4, `Subtotal — ${staffName}`, 'FF4a148c', 'FFede7f6', thin, 9);
        writeAmt(row, 5, ss.BillAmount,    'FF4a148c', 'FFede7f6', thin);
        writeAmt(row, 6, ss.ToCollect,     'FFd97706', 'FFede7f6', thin);
        for (let c = 7; c <= 13; c++) {
          const cl = cell(row, c);
          cl.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFede7f6' } };
          cl.border = thin;
        }
        writeAmt(row, 14, ss.ReceiptAmount, 'FF2e7d32', 'FFede7f6', thin);
        const cl15 = cell(row, 15);
        cl15.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFede7f6' } };
        cl15.border = thin;
        setH(row, 14); row++;
      });

      // Branch total
      const bs = calcSubtotal(allBranchRows);
      writeLabel(row, 1, 4, `Branch Total — ${branchName}`, 'FF0a3880', 'FFbbdefb', thin, 10);
      writeAmt(row, 5, bs.BillAmount,    'FF0a3880', 'FFbbdefb', thin);
      writeAmt(row, 6, bs.ToCollect,     'FFd97706', 'FFbbdefb', thin);
      for (let c = 7; c <= 13; c++) {
        const cl = cell(row, c);
        cl.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFbbdefb' } };
        cl.border = thin;
      }
      writeAmt(row, 14, bs.ReceiptAmount, 'FF2e7d32', 'FFbbdefb', thin);
      const blCl = cell(row, 15);
      blCl.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFbbdefb' } };
      blCl.border = thin;
      setH(row, 15); row++;
    });

    // ── Grand total ─────────────────────────────────────────────────────────
    writeLabel(row, 1, 4, 'GRAND TOTAL', 'FFFFFFFF', 'FF0a3880', thin, 10);
    writeAmt(row, 5, totals.BillAmount,    'FFFFFFFF', 'FF0a3880', thin);
    writeAmt(row, 6, totals.ToCollect,     'FFFFFFFF', 'FF0a3880', thin);
    for (let c = 7; c <= 13; c++) {
      const cl = cell(row, c);
      cl.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0a3880' } };
      cl.border = thin;
    }
    writeAmt(row, 14, totals.ReceiptAmount, 'FFFFFFFF', 'FF0a3880', thin);
    const gtCl = cell(row, 15);
    gtCl.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0a3880' } };
    gtCl.border = thin;
    setH(row, 16);

    // ── Save ─────────────────────────────────────────────────────────────────
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `CashOnDeliveryPaymentStatus_${tmpType}_${fromDate}_${toDate}.xlsx`);
  };

  // ─── KPI strip ─────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const collected  = filtered.filter(r => isCollectedStatus(r.PaymentStatus)).length;
    const pending    = filtered.filter(r => (r.PaymentStatus || '').toLowerCase() === 'pending').length;
    const partial    = filtered.filter(r => (r.PaymentStatus || '').toLowerCase() === 'partial').length;
    return [
      { label: 'Total Bills',    value: filtered.length,            color: PRIMARY_MID,  bg: '#e3f2fd' },
      { label: 'Bill Amount',    value: fmt(totals.BillAmount),     color: '#0277bd',    bg: '#e1f5fe' },
      { label: 'To Collect',     value: fmt(totals.ToCollect),      color: '#e65100',    bg: '#fff3e0' },
      { label: 'Collected',      value: fmt(totals.ReceiptAmount),  color: '#2e7d32',    bg: '#e8f5e9' },
      { label: '✔ Received',     value: collected,                   color: '#059669',    bg: '#d1fae5' },
      { label: '⏳ Pending',     value: pending,                     color: '#d97706',    bg: '#fef3c7' },
      { label: '◑ Partial',      value: partial,                     color: '#1d4ed8',    bg: '#dbeafe' },
    ];
  }, [filtered, totals]);

  // ─── Group by Branch → DeliveryStaff ─────────────────────────────────────
  const groupedByBranch = useMemo(() => {
    // outer: Branch → inner: DeliveryStaff → rows
    const branchMap = new Map();
    filtered.forEach(r => {
      const branch = r.BranchName    || 'Unknown Branch';
      const staff  = r.DeliveryStaff || 'Unassigned';
      if (!branchMap.has(branch)) branchMap.set(branch, new Map());
      const staffMap = branchMap.get(branch);
      if (!staffMap.has(staff)) staffMap.set(staff, []);
      staffMap.get(staff).push(r);
    });
    // convert to array: [ [branchName, [ [staffName, rows[]], ... ] ], ... ]
    return Array.from(branchMap.entries()).map(([branch, staffMap]) => [
      branch,
      Array.from(staffMap.entries()),
    ]);
  }, [filtered]);

  const calcSubtotal = (rows) => rows.reduce((acc, r) => {
    const received   = isCollectedStatus(r.PaymentStatus);
    const receiptAmt = parseFloat(r.ReceiptAmount) || (received ? (parseFloat(r.ToCollect) || 0) : 0);
    return {
      BillAmount:    acc.BillAmount    + (parseFloat(r.BillAmount) || 0),
      ToCollect:     acc.ToCollect     + (parseFloat(r.ToCollect)  || 0),
      ReceiptAmount: acc.ReceiptAmount + receiptAmt,
    };
  }, { BillAmount: 0, ToCollect: 0, ReceiptAmount: 0 });

  // kept for backward-compat with Excel export
  const branchSubtotal = calcSubtotal;

  // ─── Table styles ──────────────────────────────────────────────────────────
  const th = {
    padding: '8px 10px', fontWeight: 700, fontSize: 10,
    whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)',
    textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.3px',
  };
  const td  = { padding: '7px 10px', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'left' };
  const tdR = { padding: '7px 10px', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right' };
  const tdC = { padding: '7px 10px', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'center' };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter','Segoe UI',sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading COD Payment Status Report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.09)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* ── Toolbar ── */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '6px 12px',
          background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 60%, ${PRIMARY_MID} 100%)`,
          gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none',
          boxShadow: '0 2px 8px rgba(13,71,161,0.35)',
        }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', letterSpacing: '0.3px' }}>
              COD Payment Status Report
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap' }}>
              Cash on Delivery payment collection status
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 4 }} />

          {/* Search */}
          <input
            placeholder="Search bill / customer / staff..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
              padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 180, outline: 'none',
            }}
          />

          {/* Type filter */}
          <select
            value={tmpType}
            onChange={e => setTmpType(e.target.value)}
            style={{
              height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
              padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)',
              color: '#fff', colorScheme: 'dark', flexShrink: 0, outline: 'none', cursor: 'pointer',
            }}
          >
            {TMP_TYPES.map(t => (
              <option key={t.value} value={t.value} style={{ background: PRIMARY, color: '#fff' }}>
                {t.label}
              </option>
            ))}
          </select>

          {/* Date range */}
          <input
            type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }}
          />
          <input
            type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }}
          />

          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── KPI Strip ── */}
        {fetched && filtered.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 0, borderBottom: '1px solid #e3f2fd', overflowX: 'auto', scrollbarWidth: 'none' }}>
            {kpis.map((k, i) => (
              <div key={k.label} style={{
                flex: 1, minWidth: 90, padding: '7px 10px', textAlign: 'center',
                background: k.bg, borderRight: i < kpis.length - 1 ? '1px solid #e3f2fd' : 'none',
              }}>
                <div style={{ fontSize: 9, color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{k.label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: k.color, lineHeight: 1.3, whiteSpace: 'nowrap' }}>{k.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Meta row ── */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>{fmtDate(fromDate)} — {fmtDate(toDate)}</span>
            <span>·</span>
            <span>Type: <strong>{tmpType}</strong></span>
            <span>·</span>
            <span><strong>{filtered.length}</strong> record{filtered.length !== 1 ? 's' : ''}{search && ` (filtered from ${data.length})`}</span>
          </div>
        )}

        {/* ── Table ── */}
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY_MID} #e3f2fd` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1400 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_DARK, color: '#fff' }}>
                <th style={{ ...th, textAlign: 'center', minWidth: 36 }}>#</th>
                <th style={th}>Bill No</th>
                <th style={th}>Bill Date</th>
                <th style={{ ...th, minWidth: 160 }}>Customer</th>
                <th style={{ ...th, textAlign: 'right' }}>Bill Amount</th>
                <th style={{ ...th, textAlign: 'right' }}>To Collect</th>
                <th style={{ ...th, textAlign: 'center' }}>Payment Status</th>
                <th style={th}>Payment Mode</th>
                <th style={th}>Collected Date</th>
                <th style={{ ...th, textAlign: 'center' }}>Verification</th>
                <th style={{ ...th, minWidth: 140 }}>Remarks</th>
                <th style={th}>Receipt No</th>
                <th style={th}>Receipt Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Receipt Amount</th>
                <th style={th}>Receipt Pay Mode</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched
                      ? 'No records found for the selected criteria'
                      : 'Select date range and click Refresh to load report'}
                  </td>
                </tr>
              ) : (() => {
                let globalIdx = 0;
                return groupedByBranch.map(([branchName, staffGroups]) => {
                  const allBranchRows = staffGroups.flatMap(([, rows]) => rows);
                  const branchSub    = calcSubtotal(allBranchRows);

                  return (
                    <React.Fragment key={branchName}>

                      {/* ── Branch header ── */}
                      <tr>
                        <td colSpan={15} style={{
                          background: `linear-gradient(90deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
                          color: '#fff', fontWeight: 700, fontSize: 11,
                          padding: '5px 12px', letterSpacing: '0.3px',
                          borderTop: `2px solid ${PRIMARY_MID}`,
                          borderBottom: '1px solid rgba(255,255,255,0.15)',
                        }}>
                          🏢 {branchName}
                          <span style={{ marginLeft: 10, fontSize: 9, fontWeight: 400, background: 'rgba(255,255,255,0.18)', borderRadius: 4, padding: '1px 7px', verticalAlign: 'middle' }}>
                            {allBranchRows.length} record{allBranchRows.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>

                      {staffGroups.map(([staffName, staffRows]) => {
                        const staffSub = calcSubtotal(staffRows);
                        return (
                          <React.Fragment key={`${branchName}-${staffName}`}>

                            {/* ── Delivery Staff sub-header ── */}
                            <tr>
                              <td colSpan={15} style={{
                                background: '#e8eaf6',
                                color: PRIMARY_DARK, fontWeight: 700, fontSize: 11,
                                padding: '4px 20px', letterSpacing: '0.2px',
                                borderTop: '1px solid #c5cae9',
                                borderBottom: '1px solid #c5cae9',
                              }}>
                                🚚 {staffName}
                                <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 400, background: 'rgba(13,71,161,0.12)', borderRadius: 4, padding: '1px 7px', verticalAlign: 'middle' }}>
                                  {staffRows.length} bill{staffRows.length !== 1 ? 's' : ''}
                                </span>
                              </td>
                            </tr>

                            {/* ── Data rows ── */}
                            {staffRows.map((r, i) => {
                              const rowIdx  = globalIdx++;
                              const psBadge = paymentStatusBadge(r.PaymentStatus);
                              const vsBadge = verificationBadge(r.VerificationStatus);
                              const rowBg   = i % 2 === 0 ? '#fff' : '#f3f4fd';
                              return (
                                <tr
                                  key={r.SaleId || rowIdx}
                                  style={{ background: rowBg, borderBottom: '1px solid #f0f0f0' }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#e3f2fd'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                                >
                                  <td style={{ ...tdC, color: '#64748b' }}>{rowIdx + 1}</td>
                                  <td style={{ ...td, fontWeight: 600, color: PRIMARY }}>{r.BillNo || '-'}</td>
                                  <td style={td}>{fmtDate(r.BillDate)}</td>
                                  <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.Customer || '-'}</td>
                                  <td style={{ ...tdR, fontWeight: 600 }}>{fmt(r.BillAmount)}</td>
                                  <td style={{ ...tdR, fontWeight: 600, color: isCollectedStatus(r.PaymentStatus) ? '#059669' : (parseFloat(r.ToCollect) || 0) > 0 ? '#d97706' : '#059669' }}>
                                    {fmt(r.ToCollect)}
                                  </td>
                                  <td style={tdC}>
                                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: psBadge.bg, color: psBadge.color }}>
                                      {r.PaymentStatus || '-'}
                                    </span>
                                  </td>
                                  <td style={td}>{r.PaymentMode || '-'}</td>
                                  <td style={td}>{r.CollectedDate ? fmtDate(r.CollectedDate) : '-'}</td>
                                  <td style={tdC}>
                                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: vsBadge.bg, color: vsBadge.color }}>
                                      {r.VerificationStatus || '-'}
                                    </span>
                                  </td>
                                  <td style={{ ...td, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', color: '#64748b' }}>{r.Remarks || '-'}</td>
                                  <td style={{ ...td, fontWeight: 600, color: '#1b5e20' }}>{r.ReceiptNo || '-'}</td>
                                  <td style={td}>{r.ReceiptDate ? fmtDate(r.ReceiptDate) : '-'}</td>
                                  <td style={{ ...tdR, fontWeight: 600, color: '#2e7d32' }}>
                                    {fmt(parseFloat(r.ReceiptAmount) || (isCollectedStatus(r.PaymentStatus) ? parseFloat(r.ToCollect) : 0))}
                                  </td>
                                  <td style={td}>{r.ReceiptPaymentMode || '-'}</td>
                                </tr>
                              );
                            })}

                            {/* ── Staff subtotal ── */}
                            <tr>
                              <td colSpan={4} style={{ ...td, paddingLeft: 20, textAlign: 'right', fontWeight: 700, fontSize: 10, background: '#ede7f6', color: '#4a148c', borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }}>
                                Subtotal — {staffName}
                              </td>
                              <td style={{ ...tdR, fontWeight: 700, background: '#ede7f6', color: '#4a148c', borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }}>{fmt(staffSub.BillAmount)}</td>
                              <td style={{ ...tdR, fontWeight: 700, background: '#ede7f6', color: '#d97706',  borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }}>{fmt(staffSub.ToCollect)}</td>
                              <td colSpan={7} style={{ background: '#ede7f6', borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }} />
                              <td style={{ ...tdR, fontWeight: 700, background: '#ede7f6', color: '#2e7d32',  borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }}>{fmt(staffSub.ReceiptAmount)}</td>
                              <td style={{ background: '#ede7f6', borderTop: '1px solid #d1c4e9', borderBottom: '1px solid #d1c4e9' }} />
                            </tr>

                          </React.Fragment>
                        );
                      })}

                      {/* ── Branch subtotal ── */}
                      <tr>
                        <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, fontSize: 11, background: '#e3f2fd', color: PRIMARY_DARK, borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }}>
                          Branch Total — {branchName}
                        </td>
                        <td style={{ ...tdR, fontWeight: 700, background: '#e3f2fd', color: PRIMARY_DARK, borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }}>{fmt(branchSub.BillAmount)}</td>
                        <td style={{ ...tdR, fontWeight: 700, background: '#e3f2fd', color: '#d97706',    borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }}>{fmt(branchSub.ToCollect)}</td>
                        <td colSpan={7} style={{ background: '#e3f2fd', borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }} />
                        <td style={{ ...tdR, fontWeight: 700, background: '#e3f2fd', color: '#2e7d32',    borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }}>{fmt(branchSub.ReceiptAmount)}</td>
                        <td style={{ background: '#e3f2fd', borderTop: '2px solid #90caf9', borderBottom: '2px solid #64b5f6' }} />
                      </tr>

                    </React.Fragment>
                  );
                });
              })()}
            </tbody>

            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: '#e3f2fd', color: PRIMARY_DARK, fontWeight: 700, position: 'sticky', bottom: 0, boxShadow: '0 -2px 6px rgba(13,71,161,0.12)' }}>
                  <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>Totals</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmt(totals.BillAmount)}</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#d97706' }}>{fmt(totals.ToCollect)}</td>
                  <td colSpan={7} style={td} />
                  <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(totals.ReceiptAmount)}</td>
                  <td style={td} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CashOnDeliveryPaymentStatusReport;
